import { useContext } from 'react'
import { object, bool } from 'prop-types'
import { utils } from '@digital-commerce/shared-utils'

import localeContext from '../../../contexts/Locale'
import useTranslate from '../../../hooks/useTranslate'

import PriceBreakdownContainer from '../../molecules/CartItem/PriceBreakdown/PriceBreakdownContainer'
import {
  FinalPrice,
  ItemsPrice,
  OtherChargesAndCreditsContainer
} from '../../molecules/CartItem/PriceBreakdown'
import {
  buildIncludedDevicesList,
  buildAdditionalDevicesList,
  buildRecurringDiscountsList,
  buildSWSRecurringDiscount,
  buildAdditionalChargesList,
  buildOneTimeAdditionalDevicesList,
  getTotalPriceForFirstMonthAdditionalDevices,
  buildNonRecurringDiscountAndCreditList
} from './SmartHomeSecurity/utils'
import HeadingWithSubItems from '../../molecules/CartItem/PriceBreakdown/HeadingWithSubItems'
import NameAndPrice from '../../atoms/ItemDetails/NameAndPrice'
import {
  buildSBSAdditionalChargesList,
  buildSBSAdditionalDevicesList,
  buildSBSIncludedDevicesList
} from './SecureBusiness/utils'
import { isAffiliateCampaign } from '../../../utils/cart'

const {
  cart: { getMonthlyTotalPrice }
} = utils

const OfferDetails = ({ securityService, cart, isSecureBusiness }) => {
  const translate = useTranslate()
  const { lang } = useContext(localeContext)
  const monthlyTotalPrice = getMonthlyTotalPrice(securityService)?.subTotal
  const isAffiliates = isAffiliateCampaign(cart)

  const includedDevicesData = isSecureBusiness
    ? buildSBSIncludedDevicesList(securityService, lang)
    : buildIncludedDevicesList(securityService, lang)

  const additionalDevicesData = isSecureBusiness
    ? buildSBSAdditionalDevicesList(securityService, lang)
    : buildAdditionalDevicesList(securityService, lang)

  const recurringDiscountsData = buildRecurringDiscountsList(
    securityService,
    lang,
    translate,
    isAffiliates
  )
  const smartwearRecurringDiscountData = buildSWSRecurringDiscount(securityService, lang)

  const additionalChargesData = isSecureBusiness
    ? buildSBSAdditionalChargesList(cart, lang)
    : buildAdditionalChargesList(cart, lang)

  const oneTimePayAdditionalDevicesData = buildOneTimeAdditionalDevicesList(securityService, lang)
  const oneTimeDevicesTotalPrice = getTotalPriceForFirstMonthAdditionalDevices(
    oneTimePayAdditionalDevicesData,
    securityService
  )
  const oneTimeDiscountsAndCreditsData = buildNonRecurringDiscountAndCreditList(
    securityService,
    cart,
    lang,
    translate,
    isAffiliates
  )

  const includedDevices = (
    <HeadingWithSubItems
      headingText={translate('devices.devicesIncluded')}
      subItems={includedDevicesData}
    />
  )

  const additionalDevices = (
    <HeadingWithSubItems
      headingText={translate('devices.additionalDevices')}
      subItems={additionalDevicesData}
    />
  )

  const recurringSWSDiscounts = smartwearRecurringDiscountData && (
    <NameAndPrice
      id={smartwearRecurringDiscountData.id}
      name={smartwearRecurringDiscountData.name}
      price={smartwearRecurringDiscountData.price}
      prefixIcon={smartwearRecurringDiscountData.prefixIcon}
    />
  )

  const recurringDiscounts = recurringDiscountsData?.map((discount) => (
    <NameAndPrice
      id={discount.id}
      key={discount.id}
      name={discount.name}
      price={discount.price}
      prefixIcon={discount.prefixIcon}
    />
  ))

  const additionalCharges = (
    <HeadingWithSubItems
      headingText={translate('additionalChargesAndCredits.additionalCharges')}
      subItems={additionalChargesData}
    />
  )

  const oneTimePayAdditionalDevices = (
    <>
      <HeadingWithSubItems
        headingText={translate('devices.additionalDevices')}
        subItems={oneTimePayAdditionalDevicesData}
      />
      <FinalPrice price={oneTimeDevicesTotalPrice} overrideLabelPath="totalAdditionalDevices" />
    </>
  )

  const nonRecurringDiscountsAndCredits = (
    <HeadingWithSubItems
      headingText={translate('additionalChargesAndCredits.additionalCreditsAndDiscounts')}
      subItems={oneTimeDiscountsAndCreditsData}
    />
  )

  return (
    <>
      <PriceBreakdownContainer>
        <ItemsPrice items={[securityService]} isRecurring />
        {includedDevices}
        {additionalDevices}
        {recurringSWSDiscounts}
        {recurringDiscounts}
        <FinalPrice price={monthlyTotalPrice} isMonthly />
      </PriceBreakdownContainer>
      <OtherChargesAndCreditsContainer
        listData={[
          ...additionalChargesData,
          ...oneTimePayAdditionalDevicesData,
          ...oneTimeDiscountsAndCreditsData
        ]}
      >
        {additionalCharges}
        {oneTimePayAdditionalDevices}
        {nonRecurringDiscountsAndCredits}
      </OtherChargesAndCreditsContainer>
    </>
  )
}

export default OfferDetails

OfferDetails.propTypes = {
  securityService: object.isRequired,
  cart: object.isRequired,
  isSecureBusiness: bool
}

OfferDetails.defaultProps = {
  isSecureBusiness: false
}
